<script setup>
import { computed, inject, onMounted, ref, watch } from "vue";
import ArgonSnackbar from "@/components/ArgonSnackbar.vue";
import ArgonTable from "@/components/table/ArgonTable.vue";
import ArgonStatus from "@/components/ArgonStatus.vue";
import ArgonTd from "@/components/table/ArgonTd.vue";
import { argonTableStore } from "@/_store/argonTableStore";
import { planStore } from "@/_store/planStore";
import { routeStore } from "@/_store/routeStore";
import KanbanActionsDropdown from "@/components/dropdown/ActionsDropdown.vue";
import RouteDetailModal from "@/views/routes/components/RouteDetailModal.vue";
import VehicleAvatar from "../../../../components/VehicleAvatar.vue";
import ArgonButton from "@/components/ArgonButton.vue";
import { fileUtils } from "@/utils";
import ESTATUS_ENUM from "@/Constans/EstatusEnum";
import {
  canDeletePlanRoutes,
  normalizePlanStatus,
} from "../../utils/planStatus";
import {
  formatAlertsTooltipHtml,
  formatMinutes,
  formatVolume,
  formatSecondsAsMinutes,
  formatTimeWindow,
  formatWeight,
  normalizeAlerts,
} from "../../../../utils/formatters";

const props = defineProps({
  isActive: {
    type: Boolean,
    default: false,
  },
  planId: {
    type: String,
    default: "",
  },
  planStatus: {
    type: String,
    default: "",
  },
});

const routesStore = routeStore();
const plannerStore = planStore();
const tableStore = argonTableStore();
const emit = defineEmits(["kanban-action"]);
const sweetAlert = inject("$swal", null);

const snackbar = ref(null);
const selectedRouteId = ref("");
const showRouteDetailModal = ref(false);
const savedFilters = ref([]);
const statusOptions = Object.entries(ESTATUS_ENUM.route).map(
  ([value, meta]) => ({
    label: meta?.label || value,
    value,
  })
);
const columns = Object.freeze([
  { header: "Vehículo", field: "vehicle", sort: false, position: "start" },
  { header: "Paradas", field: "stops_counter", sort: false, position: "start" },
  { header: "Tiempo en paradas", field: "stop_time", sort: false, position: "start" },
  { header: "Kilómetros", field: "km", sort: false, position: "start" },
  { header: "Peso", field: "weight", sort: false, position: "start" },
  { header: "Horario", field: "time_window", sort: false, position: "start" },
  { header: "Estado", field: "status", sort: false, position: "center" },
  { header: "", field: "actions", sort: false, position: "center", width: "w-1"}
]);
const fieldFilters = Object.freeze([
  { field: "name", description: "Vehículo" }, 
  { field: "model", description: "Modelo" },
]);
const defaultFilters = Object.freeze([
  {
    field: "status",
    label: "Estado",
    multi: true,
    options: statusOptions,
  },
  {
    field: "has_alerts",
    label: "Alertas",
    multi: false,
    options: [
      { label: "Con alertas", value: true},
      { label: "Sin alertas", value: false },
    ],
  },
]);

const normalizedPlanStatus = computed(() => normalizePlanStatus(props.planStatus));
const canDeleteRoutes = computed(() =>
  canDeletePlanRoutes(normalizedPlanStatus.value)
);
const routeActions = computed(() => [
  [
    {
      label: "Detalle",
      event: "detailRoute",
    },
  ],
  [
    {
      label: "Eliminar",
      event: "deleteRoute",
      type: "danger",
      disabled: !canDeleteRoutes.value,
    },
  ],
]);

const routeRows = computed(() => {
  const source = Array.isArray(routesStore.routes?.data) ? routesStore.routes.data : [];
  const data = source.map((routeData, index) => {
    const normalizedRoute = getRouteData(routeData);
    const warningItems = getWarningItems(normalizedRoute?.alerts);
    const rowId = toSafeString(normalizedRoute?._id || normalizedRoute?.id || `route_${index + 1}`);

      return {
        route: normalizedRoute,
        _id: rowId,
        hasWarning: warningItems.length > 0,
        warningTooltip: formatAlertsTooltipHtml(warningItems),
      };
  });

  const total = routesStore.routes?.total;

  return {
    total,
    data,
  };
});
const tableItems = computed(() => routeRows.value);

onMounted(() => {
  tableStore.clear();
  tableStore.pagination.limit = 1000;
  tableStore.changeFilters(savedFilters.value);
});

watch(
  () => [props.isActive, props.planId],
  ([isActive]) => {
    if (!isActive) return;
    tableStore.clearSelection();
    tableStore.changeFilters(savedFilters.value);
    fetchRoutes();
  },
  { immediate: true }
);

watch(
  () => tableStore.filters,
  (value) => {
    if (!props.isActive) return;
    savedFilters.value = Array.isArray(value)
      ? JSON.parse(JSON.stringify(value))
      : [];
  },
  { deep: true }
);
watch(
  () => routesStore.error,
  (value) => {
    if (!value) return;
    snackbar.value = {
      title: "Error",
      message: value?.message || "No se pudieron cargar las rutas.",
      type: "danger",
      color: "white",
    };
  }
);
watch(
  () => plannerStore.kanbanRoutesUpdated,
  (value) => {
    if (!value || !props.isActive) return;
    fetchRoutes();
  }
);

function getPosition(field) {
  const column = columns.find((col) => col.field === field);
  return column?.position || "start";
}
function toSafeString(value) {
  if (value === null || value === undefined) return "";
  return String(value).trim();
}

function closeSnackbar() {
  snackbar.value = null;
}

function closeRouteDetailModal() {
  showRouteDetailModal.value = false;
  selectedRouteId.value = "";
}

function getSelectedRouteIds() {
  if (!Array.isArray(tableStore.checkedList)) return [];
  return [
    ...new Set(
      tableStore.checkedList.map((id) => toSafeString(id)).filter(Boolean)
    ),
  ];
}

function fetchRoutes() {
  closeSnackbar();
  const pagination = tableStore.pagination;
  const query = {
    ...(tableStore.query || {}),
    plan_id: props.planId,
  };
  const sort = tableStore.sort;

  routesStore.getRoutes(pagination, query, sort);
}

function reloadPage() {
  if (!props.isActive) return;
  fetchRoutes();
}

function onSortChanged() {
  tableStore.clearPagination();
  reloadPage();
}

function getRouteData(routeItem) {
  if (!routeItem || typeof routeItem !== "object") return {};
  return routeItem;
}

function getWarningItems(alerts) {
  return normalizeAlerts(alerts);
}

function getActionsMenuId(routeItem) {
  const routeData = getRouteData(routeItem);
  const rawId = toSafeString(routeData?._id || routeData?.id || "unknown");
  return `route-row-actions-${rawId.replace(/[^a-zA-Z0-9_-]/g, "_")}`;
}

function getRouteAccentColor(routeItem) {
  const routeData = getRouteData(routeItem);
  const routeColor = toSafeString(routeData?.route_color_hex);
  if (routeColor) return routeColor;
  return "#5baee9";
}
 async function exportRoutes() {
  const bytes = await routesStore.exportar(
    tableStore.pagination,
    {
      ...(tableStore.query || {}),
      plan_id: props.planId,
    },
    tableStore.sort
  );

  fileUtils.downloadBytesFile(
    bytes,
    fileUtils.generateDownloadFileName("xlsx")
  );
}


async function onRouteAction(routeItem, action) {
  if (!routeItem) return;

  if (action?.event === "detailRoute") {
    const routeId = toSafeString(routeItem?._id || routeItem?.id);
    if (!routeId) return;
    selectedRouteId.value = routeId;
    showRouteDetailModal.value = true;
    routesStore.getRouteById(routeId).catch(() => {});
    return;
  }

  if (action?.event !== "deleteRoute") return;

  const confirmOptions = {
    title: "¿Está seguro de querer eliminar esta ruta?",
    text: "¡No podrá revertir esta acción!",
    showCancelButton: true,
    confirmButtonText: "Eliminar",
    cancelButtonText: "Cancelar",
    reverseButtons: true,
    customClass: {
      confirmButton: "btn btn-xs ms-1 bg-gradient-danger",
      cancelButton: "btn btn-xs btn-outline-primary me-1",
    },
    buttonsStyling: false,
  };

  const result =
    typeof sweetAlert === "function"
      ? await sweetAlert(confirmOptions)
      : {
          isConfirmed: window.confirm(
            "¿Está seguro de querer eliminar esta ruta?"
          ),
        };
  if (!result?.isConfirmed) return;

  const routeId = toSafeString(routeItem?._id);
  if (!routeId) {
    snackbar.value = {
      title: "Error",
      message: "No se encontro route_id para eliminar la ruta.",
      type: "danger",
      color: "white",
    };
    return;
  }

  emit("kanban-action", {
    type: "delete-route",
    payload: {
      routeId,
    },
  });
}

async function deleteSelectedRoutes() {
  const routeIds = getSelectedRouteIds();
  if (routeIds.length === 0) {
    snackbar.value = {
      title: "Error",
      message: "Debe seleccionar al menos una ruta para eliminar.",
      type: "danger",
      color: "white",
    };
    return;
  }

  if (!canDeleteRoutes.value) return;

  const confirmOptions = {
    title: "¿Está seguro de querer eliminar las rutas seleccionadas?",
    text: "No podrá revertir esta acción.",
    showCancelButton: true,
    confirmButtonText: "Eliminar",
    cancelButtonText: "Cancelar",
    reverseButtons: true,
    customClass: {
      confirmButton: "btn btn-xs ms-1 bg-gradient-danger",
      cancelButton: "btn btn-xs btn-outline-primary me-1",
    },
    buttonsStyling: false,
  };

  const result =
    typeof sweetAlert === "function"
      ? await sweetAlert(confirmOptions)
      : {
          isConfirmed: window.confirm(
            "¿Está seguro de querer eliminar las rutas seleccionadas?"
          ),
        };
  if (!result?.isConfirmed) return;

  emit("kanban-action", {
    type: "delete-routes",
    payload: {
      routeIds,
    },
  });
}

function openRouteDetail(routeItem) {
  onRouteAction(routeItem, { event: "detailRoute" });
}
</script>

<template>
  <section class="card h-100 d-flex flex-column">
    <argon-table
      :columns="columns"
      :items="tableItems"
      :field-filters="fieldFilters"
      :default-filters="defaultFilters"
      class="p-2 pb-0 flex-grow-1"
      :show-new-button="false"
      :show-check-input="true"
      :show-importar="false"
      :show-filters="true"
      :table-hover="true"
      @filter-changed="reloadPage"
      @on-reload="reloadPage"
      @sort-changed="onSortChanged"
      @page-changed="reloadPage"
      @export-data="exportRoutes"
      @on-click="(item) => openRouteDetail(item.route)"
    >
      <template #buttons>
        <div
          class="dropdown d-inline"
          data-dropdown-static
          style="--dropdown-gap: 4px"
        >
          <argon-button
            id="routesActionsDropdown"
            color="primary"
            variant="outline"
            class="dropdown-toggle me-2 btn-xs"
            data-bs-toggle="dropdown"
            data-bs-display="static"
            aria-expanded="false"
          >
            <i class="fa fa-exchange me-sm-2"></i>Acciones
          </argon-button>
          <ul
            class="dropdown-menu z-index-1030 dropdown-menu-lg-start px-2 py-3"
            aria-labelledby="routesActionsDropdown"
          >
            <li>
              <a
                class="dropdown-item border-radius-md text-danger"
                :class="{ disabled: !canDeleteRoutes }"
                @click="deleteSelectedRoutes"
                >Eliminar</a
              >
            </li>
          </ul>
        </div>
      </template>
      <template #default="{ item }">
        <argon-td
          :position="getPosition('vehicle')"
          :text-primary="item.route.name"
          :text-secondary="item.route.model"
        >
          <template #prepend>
            <vehicle-avatar
              :label="item.route.name"
              :abbreviation="item.route.abbreviation"
              :accent-color="getRouteAccentColor(item.route)"
              :size="28"
              :has-alert="item.hasWarning"
              :alert-tooltip="item.warningTooltip"
              alert-label="Alertas de ruta"
            />
          </template>
        </argon-td>
        <argon-td
          :text-primary="item.route.stops_counter"
          :position="getPosition('stops_counter')"
        />
        <argon-td
          :text-primary="formatMinutes(item.route.stop_time)"
          :position="getPosition('stop_time')"
        />
        <argon-td
          :text-primary="`${item.route.km ?? item.route.distance} km`"
          :text-secondary="formatSecondsAsMinutes(item.route.total_time)"
          :position="getPosition('km')"
        />
        <argon-td
          :text-primary="formatWeight(item.route.max_weight)"
          :text-secondary="formatVolume(item.route.max_volume)"
          :position="getPosition('weight')"
        />
        <argon-td
          :position="getPosition('time_window')"
          :text-primary="formatTimeWindow(item.route.time_window)"
        />
        <argon-td :position="getPosition('status')">
          <argon-status entity="route" :status="item.route.status" width="100px" />
        </argon-td>
        <argon-td :position="getPosition('actions')" >
            <div class="task-action-slot">
              <kanban-actions-dropdown
                class="task-menu-wrap"
                :menu-id="getActionsMenuId(item.route)"
                :actions="routeActions"
                aria-label="Acciones de ruta"
                trigger-class="task-actions-button"
                menu-class="task-menu-dropdown"
                @action="(action) => onRouteAction(item.route, action)"
              />
            </div>
        </argon-td>
      </template>
    </argon-table>
  </section>

  <route-detail-modal
    v-if="showRouteDetailModal && selectedRouteId"
    modal-id="route-table-detail-modal"
    :route-id="selectedRouteId"
    @close="closeRouteDetailModal"
  />

  <div class="position-fixed top-0 end-0 z-index-2 p-3">
    <argon-snackbar
      v-if="snackbar"
      :title="snackbar.title"
      :description="snackbar.message"
      :icon="{ component: 'ni ni-notification-70', color: snackbar.type }"
      :color="snackbar.color"
      :close-handler="closeSnackbar"
    />
  </div>
</template>

<style scoped lang="scss">
@use "../planner-surface-shared";

.task-action-slot {
  @extend .planner-action-slot;
}
.task-menu-wrap {
  @extend .planner-action-wrap;
}
.task-menu-dropdown {
  @extend .planner-action-dropdown;
}
:deep(thead th.text-center.w-1:last-child),
:deep(tbody td.text-center:last-child) {
  min-width: 48px;
  width: 48px;
}
</style>
